import {createAsyncThunk} from '@reduxjs/toolkit';
import type {RootState} from '../../store';
import PlatformApi from '../../../api/PlatformApi';
import AppApi from '../../../api/AppApi';
import AccountConstants from '../../../constants/AccountConstants';
import {ORDER_TRACKER_CONFIG} from '../../../config/orderTracker';
import {
  getOrderStatusText,
  getOrderStep,
  ORDER_STATUS_MAPPING,
} from '../../../constants/orderStatuses';
import {OrderTrackerOrder, UserOrderId} from '../types';
import {
  isToday,
  normalize,
  parseDateISO,
  todayLocalISO,
} from 'src/shared/datetime';
import {keepOrderByTTL} from '../domain/filters';
import {getAsapFlags, setAsapSelected} from '../utils/persist';

const detailCache = new Map<
  string,
  {ts: number; value: {receipt: any; detail: any}}
>();

async function loadSaleDetailCached(
  accountId: string,
  receipt: any,
): Promise<{receipt: any; detail: any}> {
  const key = `${receipt.transactionId}|${receipt.transactionDate}`;
  const now = Date.now();
  const cached = detailCache.get(key);
  if (cached && now - cached.ts < ORDER_TRACKER_CONFIG.CACHE.DETAIL_TTL_MS) {
    return cached.value;
  }
  const detail: any = await PlatformApi.loadSaleDetail(
    accountId,
    receipt.transactionId,
    receipt.transactionDate,
  );
  const value = {receipt, detail};
  detailCache.set(key, {ts: now, value});
  return value;
}

export const fetchActiveOrders = createAsyncThunk<
  OrderTrackerOrder[],
  {force?: boolean} | void,
  {state: RootState; rejectValue: string}
>(
  'orderTracker/fetchActiveOrders',
  async (_arg, {getState, rejectWithValue}) => {
    try {
      const state = getState();
      const accountId = state?.account?.account?.id;
      const locationId = state?.account?.account?.locationId;
      const userOrderIds: UserOrderId[] =
        state?.orderTracker?.userOrderIds || [];
      const prevOrders: OrderTrackerOrder[] =
        state?.orderTracker?.activeOrders || [];

      if (!accountId || !locationId) {
        throw new Error('Missing account or location ID');
      }

      const history: any = await AppApi.getAccountHistory(
        accountId,
        0,
        ORDER_TRACKER_CONFIG.API.MAX_HISTORY_ITEMS,
        AccountConstants.SALE_TYPE,
        AccountConstants.MOBILE_PURCHASES,
      );

      const items: any[] = history?.items || [];

      const seen = new Set<string>();
      const candidates = items.filter((it: any) => {
        const typeDisplay = String(
          it?.transactionTypeDisplay || '',
        ).toLowerCase();
        const isOrderAhead =
          typeDisplay === String(AccountConstants.ORDER_AHEAD).toLowerCase() ||
          typeDisplay.includes('order ahead');

        const isTransToday = isToday(String(it?.transactionDate || ''));

        const txIdStr = String(it?.transactionId);
        const txDateStr = String(it?.transactionDate || '');
        const isHinted = userOrderIds.some((u) => {
          const uid = String(u.id);
          const utrans = String(u.transId || '');
          const udate = String(u.transDate || '');
          return (
            uid === txIdStr ||
            utrans === txIdStr ||
            (!!udate && udate === txDateStr)
          );
        });

        const pick = isOrderAhead && isTransToday && isHinted;
        if (!pick) {
          return false;
        }
        const id = String(it?.transactionId);
        if (seen.has(id)) {
          return false;
        }
        seen.add(id);
        return true;
      });
      if (candidates.length === 0) {
        return [];
      }

      // Load persisted ASAP flags for candidate transactions
      const candidateIds = candidates.map((r: any) => String(r?.transactionId));
      const persistedAsapMap = await getAsapFlags(candidateIds);

      const detailResults = await Promise.allSettled(
        candidates.map((r: any) => loadSaleDetailCached(accountId, r)),
      );

      const mapped: OrderTrackerOrder[] = detailResults
        .flatMap((res) => (res.status === 'fulfilled' ? [res.value] : []))
        .map(({receipt, detail}) => {
          const data = detail?.Data || detail || {};
          const rawStatus = data?.OrderStatus ?? data?.orderAheadStatus ?? '';
          const norm = normalize(rawStatus);

          const pickupTime: string = data?.PickupTime || data?.pickupTime || '';
          const pickupDate: string = data?.PickupDate || data?.pickupDate || '';
          const pickupLocation: string =
            data?.PickupLocation || data?.pickupLocationName || 'Pickup';

          let isOrderAhead = Boolean(pickupDate && pickupTime);

          const txIdStr = String(receipt.transactionId);
          const txDateStr = String(receipt.transactionDate || '');
          const hinted = userOrderIds.find((u) => {
            const uid = String(u.id);
            const utrans = String(u.transId || '');
            const udate = String(u.transDate || '');
            return (
              uid === txIdStr ||
              utrans === txIdStr ||
              (!!udate && udate === txDateStr)
            );
          });
          const persistedAsap = Boolean(persistedAsapMap[txIdStr]);
          // Preserve previously determined OrderNow across refreshes/crashes
          const prev = prevOrders.find(
            (o) => String(o.id) === String(receipt.transactionId),
          );
          if (prev?.orderType === 'OrderNow') {
            isOrderAhead = false;
          }
          if (
            (hinted &&
              (hinted.orderType === 'OrderNow' || hinted.isAsapSelected)) ||
            persistedAsap
          ) {
            isOrderAhead = false;
            if (
              !persistedAsap &&
              hinted &&
              (hinted.orderType === 'OrderNow' || hinted.isAsapSelected)
            ) {
              try {
                setAsapSelected(txIdStr, true);
              } catch {}
            }
          }

          const currentStep = getOrderStep(rawStatus);
          const statusText = getOrderStatusText(rawStatus, isOrderAhead);
          const isReady = ORDER_STATUS_MAPPING.READY.includes(norm as any);
          const isDone = ORDER_STATUS_MAPPING.COMPLETED.includes(norm as any);

          let estimatedIso = new Date(
            Date.now() + ORDER_TRACKER_CONFIG.MINUTES_IN_MS * 15,
          ).toISOString();
          let etaText: string | undefined;

          if (isOrderAhead && pickupTime) {
            const dateIso = pickupDate || todayLocalISO();
            const hhmmss =
              pickupTime.length === 5 ? `${pickupTime}:00` : pickupTime;
            const dt = parseDateISO(`${dateIso}T${hhmmss}`);
            if (dt) {
              estimatedIso = dt.toISOString();
            }

            etaText = pickupTime;
          } else {
            etaText = undefined;
          }

          if (isOrderAhead && pickupDate && pickupTime) {
            let shouldForceAsap = false;
            const hhmmss =
              pickupTime.length === 5 ? `${pickupTime}:00` : pickupTime;
            const dt = parseDateISO(`${pickupDate}T${hhmmss}`);
            if (dt) {
              const deltaMin = Math.abs(
                (dt.getTime() - Date.now()) /
                  ORDER_TRACKER_CONFIG.MINUTES_IN_MS,
              );
              if (deltaMin <= ORDER_TRACKER_CONFIG.UX.ASAP_WINDOW_MIN) {
                shouldForceAsap = true;
              }
            }
            if (shouldForceAsap) {
              isOrderAhead = false;
              if (!persistedAsap) {
                try {
                  setAsapSelected(txIdStr, true);
                } catch {}
              }
            }
          }

          let readySeenAt = prev?.readySeenAt;
          const readyTsSec = Number(data?.readyTimestamp) || undefined;

          if (isReady || isDone) {
            if (!readySeenAt) {
              if (readyTsSec) {
                readySeenAt = readyTsSec * 1000;
              } else {
                let fallback: number | undefined;
                if (isOrderAhead && estimatedIso) {
                  const d = parseDateISO(estimatedIso);
                  fallback = d ? d.getTime() : undefined;
                }
                if (!fallback) {
                  const tx = parseDateISO(
                    `${String(receipt.transactionDate).replace(' ', 'T')}`,
                  );
                  fallback = tx ? tx.getTime() : undefined;
                }
                readySeenAt = fallback ?? Date.now();
              }
            }
          } else {
            readySeenAt = undefined;
          }
          const orderType: 'OrderNow' | 'OrderAhead' = isOrderAhead
            ? 'OrderAhead'
            : 'OrderNow';

          const order: OrderTrackerOrder = {
            id: String(receipt.transactionId),
            transId: String(receipt.transactionId),
            transDate: String(receipt.transactionDate),
            orderType,
            currentStep,
            totalSteps: 3,
            statusText,
            etaText,
            isReady: Boolean(isReady || isDone),
            pickupLocation,
            estimatedPickupTime: estimatedIso,
            pickupDateIso: isOrderAhead
              ? pickupDate || todayLocalISO()
              : String(receipt.transactionDate).split(' ')[0],
            locationId: data?.locationId || '',
            locationName: data?.Location || data?.locationName || '',
            readySeenAt,
          };

          return {order, norm};
        })
        .filter(({order, norm}) => {
          if (
            ['expired', 'canceled', 'cancelled', 'void', 'failed'].includes(
              norm,
            )
          ) {
            return false;
          }

          return isToday(order.pickupDateIso);
        })
        .map(({order}) => order);

      const ttlNow = Date.now();
      const ttlMs =
        ORDER_TRACKER_CONFIG.READY_HIDE_TTL_MIN *
        ORDER_TRACKER_CONFIG.MINUTES_IN_MS;

      const activeOrders = mapped
        .filter((o) => keepOrderByTTL(ttlNow, o, ttlMs))
        .sort((a, b) => {
          const d =
            new Date(a.estimatedPickupTime).getTime() -
            new Date(b.estimatedPickupTime).getTime();
          return d !== 0 ? d : a.id.localeCompare(b.id);
        });

      return activeOrders;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch orders',
      );
    }
  },
  {
    condition: (arg, {getState}) => {
      if (arg && (arg as any).force) {
        return true;
      }
      const currentState: any = getState();
      const lastUpdated = currentState?.orderTracker?.lastUpdated;
      const currentTime = Date.now();
      return (
        !lastUpdated ||
        currentTime - lastUpdated >=
          ORDER_TRACKER_CONFIG.UX.MIN_REFRESH_DURATION
      );
    },
  },
);
